import React, { useEffect, useState } from 'react';
import { Calendar, Clock, Lock, Unlock, Sparkles, X, RefreshCw, Heart, Lightbulb, Target, BookOpen, PencilLine, ArrowRight } from 'lucide-react';
import { Loader } from 'lucide-react';
import { sendChatMessage } from './Azurechatbot'; // Import the chat function
import JournalReflectionAudio from './JournalReflectionAudio';


export default function JournalTabs({
  activeJournalTab,
  setActiveJournalTab,
  entries,
  isEntryLocked,
  setCurrentLockAction,
  setShowPinModal,
  setPinInput,
  setSelectedEntry,
  setShowSingleEntry,
  toggleLock,
  formatDate,
  showWisdomCards,
  setShowWisdomCards,
  wisdomCards,
  currentWisdomIndex,
  setCurrentWisdomIndex,
  generatePersonalWisdomCards,
  isGeneratingWisdom,
  isGeneratingReflection,
  reflectionAudio,
  setShowPodcastSettings,
  calculateMoodStats,
  calculateStreak,
  userName,
  loadEntryImages  // ✅ ADD THIS
}) {

  // Daily Insights State
const [insightQuestions, setInsightQuestions] = useState([]);
const [insightView, setInsightView] = useState('questions'); // 'questions' or 'insight'
const [currentInsightQuestion, setCurrentInsightQuestion] = useState('');
const [currentInsightText, setCurrentInsightText] = useState('');
const [insightLoading, setInsightLoading] = useState(false);
const [insightBg, setInsightBg] = useState('');
const [customInsightQ, setCustomInsightQ] = useState('');
const [showCustomInsightInput, setShowCustomInsightInput] = useState(false);
const insightBackgroundImages = [
  '/image/studycard7.png',
  '/image/studycard6.png',
  '/image/studycard5.png',
  '/image/studycard14.png',
];

const generateInsightQuestions = async () => {
  setInsightLoading(true);

  try {
    const unlockedEntries = entries.filter(entry => !isEntryLocked(entry.id)).slice(0, 10);
    const randomBg = insightBackgroundImages[Math.floor(Math.random() * insightBackgroundImages.length)];
    setInsightBg(randomBg);
    localStorage.setItem('dailyQuestionsBg', randomBg);

    const userNameStr = String(userName || 'User');
    const entriesContext = unlockedEntries.map((e, idx) => 
      `Entry ${idx + 1}: "${e.title}"\nMood: ${e.mood}\nContent: ${e.content.substring(0, 200)}`
    ).join('\n\n');

    const moodData = calculateMoodStats();

    const prompt = `Generate exactly 3 reflective questions for ${userNameStr}.

ENTRIES:
${entriesContext}

MOOD PATTERN: ${moodData[0]?.name || 'Mixed'}

Rules:
- Each question MUST be 4-7 words
- Start with What/How/When/Why/Am I
- Use first-person (I, my, me)
- Base on actual entry patterns
- NO em dashes or special characters

Return ONLY this JSON:
{
  "questions": ["question 1", "question 2", "question 3"]
}`;

    const response = await sendChatMessage([], prompt, null);
    
    // Handle different response formats
    let responseContent = '';
    if (typeof response === 'string') {
      responseContent = response;
    } else if (response?.choices?.[0]?.message?.content) {
      responseContent = response.choices[0].message.content;
    } else if (response?.content) {
      responseContent = response.content;
    } else {
      responseContent = JSON.stringify(response);
    }

    // Extract JSON
    let cleanResponse = responseContent
      .replace(/```json/g, '')
      .replace(/```/g, '')
      .trim();
    
    const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
    
    if (jsonMatch) {
      const jsonData = JSON.parse(jsonMatch[0]);
      const validQuestions = jsonData.questions?.slice(0, 3) || [];
      
      if (validQuestions.length > 0) {
        const today = new Date().toDateString();
        localStorage.setItem('dailyQuestionsDate', today);
        localStorage.setItem('dailyQuestions', JSON.stringify(validQuestions));
        setInsightQuestions(validQuestions);
        console.log('✅ Questions generated:', validQuestions);
      } else {
        throw new Error('No valid questions in response');
      }
    } else {
      throw new Error('No JSON found');
    }
  } catch (error) {
    console.error('❌ Error generating questions:', error);
    
    // Fallback questions
    const fallback = [
      "What am I avoiding?",
      "What brings me joy?",
      "What do I need to accept?"
    ];
    setInsightQuestions(fallback);
    localStorage.setItem('dailyQuestions', JSON.stringify(fallback));
  }
  
  setInsightLoading(false);
};
const handleInsightQuestionClick = async (question) => {
  setCurrentInsightQuestion(question);
  setInsightView('insight');
  setInsightLoading(true);

  // ✅ COOLDOWN CHECK: Check cache first
  const today = new Date().toDateString();
  const cacheKey = `insight_${question}_${today}`;
  const cachedResponse = localStorage.getItem(cacheKey);
  
  if (cachedResponse) {
    console.log('✅ Using cached insight for:', question);
    try {
      const cached = JSON.parse(cachedResponse);
      setCurrentInsightText(cached.response);
      setInsightLoading(false);
      return;
    } catch (e) {
      console.error('Cache parse error:', e);
    }
  }

  // ✅ RATE LIMIT CHECK: Track API calls per session
  const apiCallsKey = 'insightApiCalls';
  const apiCallsData = localStorage.getItem(apiCallsKey);
  let apiCalls = { count: 0, resetDate: today };
  
  if (apiCallsData) {
    try {
      apiCalls = JSON.parse(apiCallsData);
      // Reset counter if it's a new day
      if (apiCalls.resetDate !== today) {
        apiCalls = { count: 0, resetDate: today };
      }
    } catch (e) {}
  }

  // ✅ LIMIT: Only allow 3 unique insights per day
  if (apiCalls.count >= 3) {
    console.warn('⚠️ Daily insight limit reached');
    
    // ✅ CALCULATE TIME UNTIL RESET based on user's local timezone
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0); // Midnight local time
    
    const hoursUntilReset = Math.ceil((tomorrow - now) / (1000 * 60 * 60));
    const minutesUntilReset = Math.ceil((tomorrow - now) / (1000 * 60));
    
    let timeMessage = '';
    if (hoursUntilReset < 1) {
      timeMessage = `in ${minutesUntilReset} minute${minutesUntilReset !== 1 ? 's' : ''}`;
    } else {
      timeMessage = `in ${hoursUntilReset} hour${hoursUntilReset !== 1 ? 's' : ''}`;
    }
    
    setCurrentInsightText(`You've reached your daily limit of 3 unique insights. Come back ${timeMessage} for more, or ask one of your previous questions!`);
    setInsightLoading(false);
    return;
  }

  const unlockedEntries = entries.slice(0, 15).filter(entry => !isEntryLocked(entry.id));
  const userNameStr = String(userName || 'User');
  const entriesContext = unlockedEntries.map((e, idx) => 
    `Entry ${idx + 1}: "${e.title}"\nMood: ${e.mood}\nContent: ${e.content}`
  ).join('\n\n');

  const moodData = calculateMoodStats();

  const prompt = `${userNameStr} asked: "${question}"

**THEIR ENTRIES:**
${entriesContext}

**MOOD PATTERN:** ${moodData[0]?.name || 'Mixed'}

Provide a personalized insight in EXACTLY 3 SHORT sentences (10-15 words each). Reference specific patterns from their entries. Be supportive but honest. Use second person. NO em dashes.

CRITICAL: Keep it brief - total response should be 30-45 words maximum.`;

  try {
    // ✅ CORRECT: Pass parameters in right order, no extra options
    const response = await sendChatMessage(
      [],      // messageHistory
      prompt,  // userMessage
      null,    // calendarContext
      null,    // memoryService
      null,    // systemMessage
      [],      // enabledTools
      null     // lastScheduleDisplayed
    );
    
    // Extract content from different response formats
    let insightText = '';
    
    if (typeof response === 'string') {
      insightText = response;
    } else if (response?.choices?.[0]?.message?.content) {
      // Azure API format
      insightText = response.choices[0].message.content;
    } else if (response?.content) {
      insightText = response.content;
    } else if (response?.message?.content) {
      insightText = response.message.content;
    } else {
      insightText = JSON.stringify(response);
    }
    
    // Clean up the text
    insightText = insightText
      .replace(/```json/g, '')
      .replace(/```/g, '')
      .replace(/—/g, ',')
      .replace(/–/g, ',')
      .trim();
    
    // ✅ Check if response is empty due to token limit or API error
    if (!insightText || insightText.length < 10) {
      console.error('❌ Empty or truncated response:', response);
      setCurrentInsightText("Service is down at the moment. Please try again or contact support@preptune.com");
    } else {
      setCurrentInsightText(insightText);
      
      // ✅ CACHE THE RESPONSE for future use
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          response: insightText,
          timestamp: Date.now(),
          question: question
        }));
        console.log('✅ Cached insight response for:', question);
      } catch (cacheError) {
        console.warn('Failed to cache response:', cacheError);
      }
      
      // ✅ INCREMENT API CALL COUNTER
      apiCalls.count += 1;
      localStorage.setItem(apiCallsKey, JSON.stringify(apiCalls));
      console.log(`✅ API calls today: ${apiCalls.count}/3`);
    }
    
  } catch (error) {
    console.error('Error:', error);
    setCurrentInsightText("Service is down at the moment. Please try again or contact support@preptune.com");
  }

  setInsightLoading(false);
};

useEffect(() => {
  if (unlockedEntriesCount < 3) return;

  const today = new Date().toDateString();
  const cachedDate = localStorage.getItem('dailyQuestionsDate');
  const cachedQuestions = localStorage.getItem('dailyQuestions');

  if (cachedDate === today && cachedQuestions) {
    try {
      setInsightQuestions(JSON.parse(cachedQuestions));
      const savedBg = localStorage.getItem('dailyQuestionsBg');
      if (savedBg) {
        setInsightBg(savedBg);
      } else {
        const randomBg = insightBackgroundImages[Math.floor(Math.random() * insightBackgroundImages.length)];
        setInsightBg(randomBg);
      }
      return;
    } catch (e) {}
  }

  generateInsightQuestions();
}, []);
  // Get count of unlocked entries
  const unlockedEntriesCount = entries.filter(entry => !isEntryLocked(entry.id)).length;
const [affirmation, setAffirmation] = useState({
  title: "Welcome to Your Journal",
  message: "Start writing to receive personalized insights and affirmations based on your journey.",
  image: "/image/session-img31.png",
  tag: "Getting Started"
});
const [isGeneratingAffirmation, setIsGeneratingAffirmation] = useState(false);
const [showReflectionAudio, setShowReflectionAudio] = useState(false);

  // Daily Affirmations
const staticDailyAffirmations = [
  {
    id: 1,
    text: "Your thoughts and feelings matter. Every word you write is a step toward understanding yourself better.",
    image: '/image/session-img33.png'
  },
  {
    id: 2,
    text: "Writing is not just remembering—it's discovering who you are and who you're becoming.",
    image: '/image/session-img31.png'
  },
  {
    id: 3,
    text: "Each entry is a gift to your future self. Keep going, you're building something beautiful.",
    image: '/image/session-img32.png'
  }
];

const [dailyAffirmation, setDailyAffirmation] = useState(null);
const [isGeneratingDailyAffirmation, setIsGeneratingDailyAffirmation] = useState(false);
const [showPrivacyLock, setShowPrivacyLock] = useState(false);

// Default affirmations for rotation when user has no entries
const defaultAffirmations = [
  {
    title: "Positive Energy",
    message: "Each entry is a gift to your future self. Keep going and stay consistent!",
    image: "/image/session-img31.png",
    tag: "Motivational Quote"
  },
  {
    title: "Inner Peace",
    message: "Writing is not just remembering, it's a way of discovering yourself.",
    image: "/image/session-img32.png",
    tag: "Affirmation"
  },
  {
    title: "Daily Wisdom",
    message: "In the quiet moments of writing, you'll find clarity and calm.",
    image: "/image/session-img33.png",
    tag: "Motivational Quote"
  },
  {
    title: "Mindful Moment",
    message: "Your thoughts and feelings are valid. Take time to honor them.",
    image: "/image/session-img34.png",
    tag: "Affirmation"
  },
  {
    title: "Today's Reflection",
    message: "Every word you write is a step toward understanding yourself better.",
    image: "/image/session-img47.png",
    tag: "Affirmation"
  },
  {
    title: "Keep Growing",
    message: "Progress isn't always linear. Every entry counts, even the small ones.",
    image: "/image/session-img48.png",
    tag: "Motivational Quote"
  },
  {
    title: "Self Discovery",
    message: "Through writing, you're not just recording life—you're understanding it.",
    image: "/image/session-img50.png",
    tag: "Affirmation"
  },
  {
    title: "Gentle Reminder",
    message: "Be patient with yourself. Growth takes time, and you're doing great.",
    image: "/image/session-img49.png",
    tag: "Affirmation"
  }
];

const [currentDefaultIndex, setCurrentDefaultIndex] = useState(0);

// Available images for personalized affirmations
const affirmationImages = [
  '/image/session-img31.png',
  '/image/session-img32.png',
  '/image/session-img33.png',
  '/image/session-img34.png',
  '/image/session-img36.png',
  '/image/session-img49.png',
  '/image/session-img39.png',
  '/image/session-img40.png',
  '/image/session-img41.png',
  '/image/session-img42.png',
  '/image/session-img43.png',
  '/image/session-img44.png',
];

// Function to generate personalized affirmation
const generatePersonalizedAffirmation = async () => {
  if (entries.length === 0) {
    console.log('No entries yet, using default affirmations');
    return;
  }

  // Check if we generated an affirmation in the last 24 hours
  const lastGen = localStorage.getItem('lastAffirmationGeneration');
  if (lastGen) {
    const hoursSinceLastGen = (Date.now() - parseInt(lastGen)) / (1000 * 60 * 60);
    if (hoursSinceLastGen < 24) {
      console.log('Already generated affirmation today');
      const saved = localStorage.getItem('personalizedAffirmation');
      if (saved) {
        setAffirmation(JSON.parse(saved));
      }
      return;
    }
  }

  setIsGeneratingAffirmation(true);

  try {
    const recentEntries = entries.slice(0, 5);
    const unlockedEntries = recentEntries.filter(entry => !isEntryLocked(entry.id));
    
    if (unlockedEntries.length === 0) {
      console.log('All recent entries are locked, using defaults');
      setIsGeneratingAffirmation(false);
      return;
    }

    // ✅ USE PROPS INSTEAD:
    // const userName = String(getStoredUserName() || 'there'); // ❌ OLD
    const userNameStr = String(userName || 'there'); // ✅ NEW - use prop
    
    const entriesContext = unlockedEntries.map((e, idx) => 
      `Entry ${idx + 1}: "${e.title}"\n${e.content.substring(0, 150)}...\nMood: ${e.mood}`
    ).join('\n\n');

    const moodData = calculateMoodStats(); // ✅ Now available as prop
    const currentStreak = calculateStreak(entries); // ✅ Now available as prop

    const prompt = `You are ${userNameStr}'s supportive journal companion. Based on their recent journal entries, create ONE personalized affirmation message.

**RECENT ENTRIES:**
${entriesContext}

**CONTEXT:**
- Current streak: ${currentStreak} days
- Dominant mood: ${moodData[0].name}
- Total entries: ${entries.length}

**YOUR TASK:**
Create ONE warm, encouraging affirmation that:
1. References something specific from their recent entries (a theme, feeling, or accomplishment)
2. Is supportive and uplifting
3. Feels personal to THEIR journey
4. Is 2-3 sentences maximum
5. Uses their name (${userNameStr}) ONCE at the beginning

**TONE:** Warm, supportive friend. Natural and conversational. NOT therapy-speak.

**EXAMPLES:**
- "${userNameStr}, you've been exploring such deep questions about purpose lately. That courage to sit with uncertainty is real growth."
- "${userNameStr}, I notice you're celebrating small wins more often now. That shift in perspective is beautiful to witness."
- "${userNameStr}, the way you showed up for yourself this week—even when it was hard—that's the kind of consistency that creates lasting change."

Return ONLY a JSON object with this structure:
{
  "title": "3-5 word uplifting title",
  "message": "Your personalized 2-3 sentence affirmation here",
  "tag": "Personal Affirmation"
}

No markdown, just JSON.`;

    const response = await sendChatMessage([], prompt, null);
    
    const responseContent = typeof response === 'string' 
      ? response 
      : response.content || response.message?.content || JSON.stringify(response);
    
    let affirmationData;
    try {
      let cleanResponse = responseContent.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        affirmationData = JSON.parse(jsonMatch[0]);
        
        if (affirmationData.title && affirmationData.message) {
          const randomImage = affirmationImages[Math.floor(Math.random() * affirmationImages.length)];
          
          const newAffirmation = {
            title: affirmationData.title,
            message: affirmationData.message,
            image: randomImage,
            tag: affirmationData.tag || "Personal Affirmation",
            isPersonalized: true
          };

          setAffirmation(newAffirmation);
          localStorage.setItem('personalizedAffirmation', JSON.stringify(newAffirmation));
          localStorage.setItem('lastAffirmationGeneration', Date.now().toString());
          
          console.log('✅ Personalized affirmation generated');
        } else {
          throw new Error('Invalid affirmation structure');
        }
      } else {
        throw new Error('No JSON found in response');
      }
    } catch (parseError) {
      console.error('❌ Parse error:', parseError);
      
      const randomImage = affirmationImages[Math.floor(Math.random() * affirmationImages.length)];
      
      const fallbackAffirmation = {
        title: "Your Journey Matters",
        message: `${userNameStr}, you've written ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}. Each one is a step toward understanding yourself better. Keep going—you're building something meaningful here.`,
        image: randomImage,
        tag: "Personal Affirmation",
        isPersonalized: true
      };
      
      setAffirmation(fallbackAffirmation);
      localStorage.setItem('personalizedAffirmation', JSON.stringify(fallbackAffirmation));
      localStorage.setItem('lastAffirmationGeneration', Date.now().toString());
    }
    
  } catch (error) {
    console.error('❌ Error generating affirmation:', error);
  } finally {
    setIsGeneratingAffirmation(false);
  }
};

// Load saved personalized affirmation on mount
useEffect(() => {
  const loadSavedAffirmation = () => {
    try {
      if (entries.length === 0) {
        // Use default affirmations rotation
        setAffirmation(defaultAffirmations[0]);
        return;
      }

      const saved = localStorage.getItem('personalizedAffirmation');
      if (saved) {
        setAffirmation(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved affirmation:', error);
    }
  };
  
  loadSavedAffirmation();
}, []);

// Auto-generate personalized affirmation when entries change
useEffect(() => {
  if (entries.length === 0) return;
  
  const lastEntryDate = localStorage.getItem('lastAffirmationEntryDate');
  const newestEntryDate = entries[0]?.date?.getTime();
  
  // Only generate if there's a NEW entry since last generation
  if (!lastEntryDate || parseInt(lastEntryDate) < newestEntryDate) {
    const lastGen = localStorage.getItem('lastAffirmationGeneration');
    const hoursSinceLastGen = lastGen ? (Date.now() - parseInt(lastGen)) / (1000 * 60 * 60) : 999;
    
    // Generate if 24+ hours passed OR first time
    if (hoursSinceLastGen >= 24 || !lastGen) {
      const timer = setTimeout(() => {
        generatePersonalizedAffirmation();
        localStorage.setItem('lastAffirmationEntryDate', newestEntryDate.toString());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }
}, [entries]);
// Rotate default affirmations if no entries (every 8 seconds)
useEffect(() => {
  if (entries.length === 0) {
    const interval = setInterval(() => {
      setCurrentDefaultIndex((prev) => (prev + 1) % defaultAffirmations.length);
      setAffirmation(defaultAffirmations[(currentDefaultIndex + 1) % defaultAffirmations.length]);
    }, 8000);

    return () => clearInterval(interval);
  }
}, [entries.length, currentDefaultIndex]);
const generateDailyAffirmation = async () => {
  const unlockedCount = entries.filter(entry => !isEntryLocked(entry.id)).length;
  
  // Check if user wrote in last 24 hours
  const lastEntryTime = entries.length > 0 ? new Date(entries[0].date).getTime() : 0;
  const hoursSinceLastEntry = (Date.now() - lastEntryTime) / (1000 * 60 * 60);
  
  // LOGIC 1: Less than 3 unlocked entries - show privacy lock
  if (unlockedCount < 3) {
    setShowPrivacyLock(true);
    setTimeout(() => {
      setShowPrivacyLock(false);
      setDailyAffirmation({
        text: staticDailyAffirmations[0].text,
        image: staticDailyAffirmations[0].image,
        isStatic: true
      });
    }, 3000);
    return;
  }
  
  // LOGIC 2: No recent entry (>24 hours) - use static
  if (hoursSinceLastEntry > 24) {
    console.log('No recent entry, using static affirmations');
    setDailyAffirmation({
      text: staticDailyAffirmations[0].text,
      image: staticDailyAffirmations[0].image,
      isStatic: true
    });
    return;
  }
  
  // LOGIC 3: Check if already generated today
  const lastGenDate = localStorage.getItem('lastDailyAffirmationDate');
  const today = new Date().toDateString();
  
  if (lastGenDate === today) {
    const saved = localStorage.getItem('dailyAffirmationData');
    if (saved) {
      setDailyAffirmation(JSON.parse(saved));
      return;
    }
  }
  
  setIsGeneratingDailyAffirmation(true);
  
  try {
    const recentEntries = entries.slice(0, 5);
    const unlockedEntries = recentEntries.filter(entry => !isEntryLocked(entry.id));
    
    const userNameStr = String(userName || 'there');
    
    const entriesContext = unlockedEntries.map((e, idx) => 
      `Entry ${idx + 1}: "${e.title}"\nMood: ${e.mood}\n${e.content.substring(0, 100)}...`
    ).join('\n\n');
    
    // Get list of already used images (NO REPEATS)
    const usedImages = JSON.parse(localStorage.getItem('usedDailyAffirmationImages') || '[]');
    const availableImages = affirmationImages.filter(img => !usedImages.includes(img));
    
    // If all images used, reset the list
    const imagesToUse = availableImages.length > 0 ? availableImages : affirmationImages;
    
    const prompt = `You are a supportive daily companion for ${userNameStr}'s journaling journey. Create ONE uplifting daily affirmation based on their recent entries.

**RECENT ENTRIES:**
${entriesContext}

**YOUR TASK:**
Create a warm, encouraging affirmation (2-3 sentences) that:
1. References a theme, feeling, or pattern from their recent writing
2. Is supportive and motivational
3. Feels personal and genuine
4. Uses natural, conversational language

**TONE:** Warm friend checking in. NOT therapy-speak or overly formal.

**EXAMPLES:**
- "You've been exploring questions about purpose lately. That kind of honest self-reflection takes real courage."
- "I notice you're celebrating small wins more often. That shift in perspective is creating positive momentum."
- "The way you showed up for yourself this week, even when it was hard—that's the foundation of lasting change."

Return ONLY the affirmation text, no JSON, no formatting. Just 2-3 sentences.`;

    const response = await sendChatMessage([], prompt, null);
    
    const responseContent = typeof response === 'string' 
      ? response 
      : response.content || response.message?.content || JSON.stringify(response);
    
    // Clean up response
    let affirmationText = responseContent
      .replace(/```json\n?/g, '')
      .replace(/```\n?/g, '')
      .replace(/^["']|["']$/g, '')
      .trim();
    
    // Pick random image from available ones (NO REPEATS)
    const selectedImage = imagesToUse[Math.floor(Math.random() * imagesToUse.length)];
    
    // Update used images list
    const newUsedImages = [...usedImages, selectedImage];
    if (newUsedImages.length >= affirmationImages.length) {
      localStorage.setItem('usedDailyAffirmationImages', JSON.stringify([selectedImage]));
    } else {
      localStorage.setItem('usedDailyAffirmationImages', JSON.stringify(newUsedImages));
    }
    
    const newAffirmation = {
      text: affirmationText,
      image: selectedImage,
      isStatic: false,
      generatedAt: Date.now()
    };
    
    setDailyAffirmation(newAffirmation);
    localStorage.setItem('dailyAffirmationData', JSON.stringify(newAffirmation));
    localStorage.setItem('lastDailyAffirmationDate', today);
    
    console.log('✅ Daily affirmation generated');
    
  } catch (error) {
    console.error('❌ Error generating daily affirmation:', error);
    // Fallback to static
    const randomStatic = staticDailyAffirmations[Math.floor(Math.random() * staticDailyAffirmations.length)];
    setDailyAffirmation({
      text: randomStatic.text,
      image: randomStatic.image,
      isStatic: true
    });
  } finally {
    setIsGeneratingDailyAffirmation(false);
  }
};
  // Load and auto-generate daily affirmation
useEffect(() => {
  if (entries.length === 0) {
    setDailyAffirmation({
      text: staticDailyAffirmations[0].text,
      image: staticDailyAffirmations[0].image,
      isStatic: true
    });
    return;
  }
  
  // Try to load saved affirmation first
  const saved = localStorage.getItem('dailyAffirmationData');
  const lastGenDate = localStorage.getItem('lastDailyAffirmationDate');
  const today = new Date().toDateString();
  
  if (saved && lastGenDate === today) {
    setDailyAffirmation(JSON.parse(saved));
  } else {
    // Generate new one
    const timer = setTimeout(() => {
      generateDailyAffirmation();
    }, 1000);
    return () => clearTimeout(timer);
  }
}, [entries]);

// Rotate static affirmations if using fallback
useEffect(() => {
  if (dailyAffirmation && dailyAffirmation.isStatic) {
    const interval = setInterval(() => {
      const currentIndex = staticDailyAffirmations.findIndex(a => a.text === dailyAffirmation.text);
      const nextIndex = (currentIndex + 1) % staticDailyAffirmations.length;
      setDailyAffirmation({
        text: staticDailyAffirmations[nextIndex].text,
        image: staticDailyAffirmations[nextIndex].image,
        isStatic: true
      });
    }, 8000);
    return () => clearInterval(interval);
  }
}, [dailyAffirmation]);

const handleInsightSubmit = () => {
  const today = new Date().toDateString();
  const apiCallsKey = 'insightApiCalls';
  const apiCallsData = localStorage.getItem(apiCallsKey);
  let apiCalls = { count: 0, resetDate: today };
  
  if (apiCallsData) {
    try {
      apiCalls = JSON.parse(apiCallsData);
      if (apiCalls.resetDate !== today) {
        apiCalls = { count: 0, resetDate: today };
      }
    } catch (e) {}
  }
  
  const cacheKey = `insight_${customInsightQ}_${today}`;
  const cachedResponse = localStorage.getItem(cacheKey);
  
  if (!cachedResponse && apiCalls.count >= 3) {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const hoursUntilReset = Math.ceil((tomorrow - now) / (1000 * 60 * 60));
    alert(`You've reached your daily limit of 3 unique insights. Come back in ${hoursUntilReset} hour${hoursUntilReset !== 1 ? 's' : ''} for more!`);
    return;
  }
  
  handleInsightQuestionClick(customInsightQ);
  setShowCustomInsightInput(false);
  setCustomInsightQ('');
};
const ForYouTab = () => {
  // Handle insight submission with rate limiting
  const handleInsightSubmit = () => {
    if (!customInsightQ.trim()) return;

    // Check length in backend
    if (customInsightQ.length > 100) {
      alert('Your message is too long. Please keep it under 100 characters.');
      return;
    }

    const today = new Date().toDateString();
    const apiCallsKey = 'insightApiCalls';
    const apiCallsData = localStorage.getItem(apiCallsKey);
    let apiCalls = { count: 0, resetDate: today };

    if (apiCallsData) {
      try {
        apiCalls = JSON.parse(apiCallsData);
        if (apiCalls.resetDate !== today) {
          apiCalls = { count: 0, resetDate: today };
        }
      } catch (e) {
        apiCalls = { count: 0, resetDate: today };
      }
    }

    const cacheKey = `insight_${customInsightQ}_${today}`;
    const cachedResponse = localStorage.getItem(cacheKey);

    if (!cachedResponse && apiCalls.count >= 3) {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const hoursUntilReset = Math.ceil((tomorrow - now) / (1000 * 60 * 60));
      alert(
        `You've reached your daily limit of 3 unique insights. Come back in ${hoursUntilReset} hour${
          hoursUntilReset !== 1 ? 's' : ''
        } for more!`
      );
      return;
    }

    handleInsightQuestionClick(customInsightQ);
    setCustomInsightQ('');
    setShowCustomInsightInput(false);
  };

  if (entries.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
        <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ margin: '0 auto 16px', opacity: 0.5 }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
          Nothing to discover yet
        </h3>
        <p style={{ fontSize: '13px', color: '#9ca3af', lineHeight: '1.5' }}>
          Write a few entries to unlock personalized insights
        </p>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '40px' }}>
      {unlockedEntriesCount < 3 ? (
        // LOCKED STATE
        <div
          style={{
            marginBottom: '0',
            overflow: 'hidden',
            position: 'relative',
            borderRadius: '24px',
            border: '1px solid #e5e7eb',
          }}
        >
          <div style={{ padding: 0 }}>
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '360px',
                overflow: 'hidden',
                background: '#000',
              }}
            >
              {insightBg && (
                <>
                  <img
                    src={insightBg}
                    alt=""
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'blur(6px)',
                      transform: 'scale(1.4)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.75) 100%)',
                    }}
                  />
                </>
              )}

              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px',
                  textAlign: 'center',
                  zIndex: 2,
                }}
              >
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '8px',
                  }}
                >
                  Privacy Protected
                </div>

                <div
                  style={{
                    fontSize: '13px',
                    color: 'rgba(255,255,255,0.85)',
                    lineHeight: '1.5',
                    maxWidth: '260px',
                  }}
                >
                  You need at least 3 unlocked entries to unlock personalized insights
                </div>
                <div
                  style={{
                    display: 'inline-flex',
                    marginTop: '30px',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    background: 'rgba(245, 158, 11, 0.1)',
                    borderRadius: '12px',
                    fontSize: '13px',
                    color: '#f59e0b',
                    fontWeight: '500',
                  }}
                >
                  <Lock size={14} />
                  Currently: {unlockedEntriesCount}/3 unlocked
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // UNLOCKED STATE
        <div
          style={{
            marginBottom: '0',
            overflow: 'hidden',
            position: 'relative',
            borderRadius: '24px',
            border: '1px solid #e5e7eb',
          }}
        >
          <div style={{ padding: 0 }}>
            <div style={{ position: 'relative', width: '100%', minHeight: '300px' }}>
              {insightBg && (
                <>
                  <img
                    src={insightBg}
                    alt=""
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'blur(2px)',
                      transform: 'scale(1.5)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        'linear-gradient(180deg, rgba(0, 0, 0, 0.11) 0%, rgba(0,0,0,0.65) 100%)',
                    }}
                  />
                </>
              )}

              {insightView === 'questions' ? (
                <>
                  {/* REFRESH BUTTON */}
                  <button
                    onClick={generateInsightQuestions}
                    disabled={insightLoading}
                    style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      zIndex: 10,
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: insightLoading ? 'not-allowed' : 'pointer',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      background: 'rgba(0, 0, 0, 0.5)',
                    }}
                  >
                    <RefreshCw
                      size={18}
                      color="#ffffff"
                      style={{
                        animation: insightLoading ? 'spin 1s linear infinite' : 'none',
                      }}
                    />
                  </button>

                  {/* MAIN CONTENT */}
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '24px',
                      minHeight: '300px',
                    }}
                  >
                    {/* SELECT ONE LABEL */}
                    <p
                      style={{
                        fontSize: '15px',
                        color: '#fff',
                        margin: '0 0 20px 0',
                        fontWeight: '500',
                        textAlign: 'left',
                        textShadow: '0 1px 3px rgba(152, 152, 152, 0.3)',
                      }}
                    >
                      <span style={{ fontStyle: 'italic', color: '#ffffffff' }}>
                        select one
                      </span>
                    </p>

                    {/* QUESTION BUTTONS */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                      {insightLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                          <RefreshCw
                            size={20}
                            color="rgba(255, 255, 255, 0.9)"
                            style={{
                              animation: 'spin 1s linear infinite',
                              marginBottom: '8px',
                            }}
                          />
                          <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
                            Creating personalized questions...
                          </p>
                        </div>
                      ) : (
                        insightQuestions.map((q, i) => (
                          <button
                            key={i}
                            onClick={() => handleInsightQuestionClick(q)}
                            style={{
                              padding: '12px 18px',
                              background: 'rgba(255, 255, 255, 1)',
                              border: '1px solid rgba(255, 255, 255, 1)',
                              borderRadius: '28px',
                              color: '#1f2937',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              textAlign: 'left',
                              lineHeight: '1.4',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                          >
                            {q}
                          </button>
                        ))
                      )}
                    </div>

                    {/* SHARE ANYTHING - COLLAPSIBLE INPUT */}
                    {!showCustomInsightInput ? (
                      // BUTTON STATE
                      <button
                        onClick={() => setShowCustomInsightInput(true)}
                        style={{
                          padding: '12px 18px',
                          background: 'rgba(255, 255, 255, 0.2)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: '28px',
                          color: '#ffffff',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          textAlign: 'left',
                        }}
                      >
                        <PencilLine size={15} />
                        <span>Share anything...</span>
                      </button>
                    ) : (
                      // INPUT STATE
                      <div
                        style={{
                          padding: '10px 16px',
                          background: 'rgba(255, 255, 255, 0.32)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: '28px',
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                        }}
                      >
                        <input
                          type="text"
                          placeholder="What's on your mind?"
                          value={customInsightQ}
                          onChange={(e) => {
                            setCustomInsightQ(e.target.value);
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && customInsightQ.trim()) {
                              handleInsightSubmit();
                            }
                          }}
                          autoFocus
                          style={{
                            flex: 1,
                            border: 'none',
                            background: 'transparent',
                            fontSize: '13px',
                            outline: 'none',
                            color: '#edededff',
                            padding: '0',
                          }}
                        />

                        {/* SUBMIT BUTTON */}
                        <button
                          onClick={() => {
                            if (customInsightQ.trim()) {
                              handleInsightSubmit();
                            }
                          }}
                          disabled={!customInsightQ.trim()}
                          style={{
                            background: '#000000',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '50px',
                            padding: '8px',
                            cursor: customInsightQ.trim() ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            opacity: customInsightQ.trim() ? 1 : 0.5,
                            flexShrink: 0,
                          }}
                        >
                          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.1584 3.13508C6.35985 2.94621 6.67627 2.95642 6.86514 3.15788L10.6151 7.15788C10.7954 7.3502 10.7954 7.64949 10.6151 7.84182L6.86514 11.8418C6.67627 12.0433 6.35985 12.0535 6.1584 11.8646C5.95694 11.6757 5.94673 11.3593 6.1356 11.1579L9.565 7.49985L6.1356 3.84182C5.94673 3.64036 5.95694 3.32394 6.1584 3.13508Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                // INSIGHT RESULT VIEW
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '24px',
                    minHeight: '330px',
                  }}
                >
                  <div
                    style={{
                      background: 'rgba(255, 255, 255, 1)',
                      borderRadius: '16px',
                      padding: '12px',
                      marginBottom: '16px',
                    }}
                  >
                    <p
                      style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#1f2937',
                        margin: 0,
                        lineHeight: '1.4',
                        textAlign: 'left',
                      }}
                    >
                      {currentInsightQuestion}
                    </p>
                  </div>

                  {insightLoading ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '0',
                        marginBottom: '16px',
                      }}
                    >
                      <RefreshCw
                        size={20}
                        color="rgba(255, 255, 255, 1)"
                        style={{ animation: 'spin 1s linear infinite' }}
                      />
                      <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 1)', margin: 0 }}>
                        Analyzing your entries...
                      </p>
                    </div>
                  ) : (
                    <>
                      <div style={{ flex: 1, marginBottom: '16px' }}>
                        <p
                          style={{
                            fontSize: '14px',
                            lineHeight: '1.7',
                            color: '#fff',
                            margin: 0,
                            fontWeight: '400',
                            textAlign: 'left',
                          }}
                        >
                          {currentInsightText}
                        </p>
                      </div>

                      <button
                        onClick={() => setInsightView('questions')}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: '#ffffff',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: '24px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                        }}
                      >
                        OK
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SPIN ANIMATION */}
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </div>
  );
};
<style>{`
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`}</style>
const MeditationTab = () => {
  if (entries.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#9ca3af'
      }}>
        <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ margin: '0 auto 16px', opacity: 0.5 }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
          Start journaling to unlock meditations
        </h3>
        <p style={{ fontSize: '13px', color: '#9ca3af', lineHeight: '1.5' }}>
          Your personalized audio meditations will appear here
        </p>
      </div>
    );
  }

  // Determine time of day
  const hour = new Date().getHours();
  let timeLabel = '';
  let timeIcon = '';
  
  if (hour >= 5 && hour < 12) {
    timeLabel = 'Morning Meditation';
    timeIcon = '🌅';
  } else if (hour >= 12 && hour < 18) {
    timeLabel = 'Afternoon Meditation';
    timeIcon = '☀️';
  } else {
    timeLabel = 'Evening Meditation';
    timeIcon = '🌙';
  }

  const unlockedEntriesCount = entries.filter(entry => !isEntryLocked(entry.id)).length;

  return (
    <div style={{ paddingBottom: '40px' }}>
      <div style={{ marginBottom: '22px' }}>
        <h3 style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#1f2937',
          marginBottom: '12px',
          textAlign: 'left',
          letterSpacing: '0.3px'
        }}>
          Personalized Meditation
        </h3>
      </div>

      {/* Check if user has at least 3 UNLOCKED entries */}
      {unlockedEntriesCount < 3 ? (
        // LOCKED STATE
        <div 
          className="affirmation-card"
          style={{ 
            cursor: 'default',
            marginBottom: '0',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <div className="affirmation-inner" style={{ padding: 0 }}>
            <div style={{
              position: 'relative',
              width: '100%',
              height: '155px',
              overflow: 'hidden'
            }}>
              <img 
                src={hour >= 5 && hour < 12 ? '/image/session-img52.png' : 
                     hour >= 12 && hour < 18 ? '/image/session-img50.png' : 
                     '/image/session-img49.png'} 
                alt="Meditation" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'blur(8px)'
                }}
              />
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)'
              }} />
              
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'rgba(245, 158, 11, 0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)'
              }}>
                <Lock size={28} color="#ffffff" />
              </div>
            </div>
            
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '8px'
              }}>
                Privacy Protected
              </div>
              <div style={{
                fontSize: '13px',
                color: '#6b7280',
                lineHeight: '1.5',
                marginBottom: '16px'
              }}>
                You need at least 3 unlocked journal entries to access personalized meditations
              </div>
              
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                background: 'rgba(245, 158, 11, 0.1)',
                borderRadius: '12px',
                fontSize: '13px',
                color: '#f59e0b',
                fontWeight: '500'
              }}>
                <Lock size={14} />
                Currently: {unlockedEntriesCount}/3 unlocked
              </div>
            </div>
          </div>
        </div>
      ) : (
        // UNLOCKED STATE
        <div 
          className="affirmation-card"
          style={{ 
            cursor: 'default',
            marginBottom: '0',
            overflow: 'hidden'
          }}
        >
          <div className="affirmation-inner" style={{ padding: 0 }}>
            <div style={{
              position: 'relative',
              width: '100%',
              height: '155px',
              overflow: 'hidden'
            }}>
              <img 
                src={hour >= 5 && hour < 12 ? '/image/session-img52.png' : 
                     hour >= 12 && hour < 18 ? '/image/session-img50.png' : 
                     '/image/session-img49.png'} 
                alt="Meditation" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.5) 100%)'
              }} />
              
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '12px',
                padding: '6px 10px',
                background: 'rgba(255, 255, 255, 0.64)',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '500',
                color: '#1f2937',
                letterSpacing: '0.3px'
              }}>
                Listen anytime
              </div>
              
              <div style={{
                position: 'absolute',
                bottom: '12px',
                left: '12px',
                right: '12px',
                textAlign: 'left'
              }}>
                <div style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#ffffff',
                  marginBottom: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{ fontSize: '14px' }}>{timeIcon}</span>
                  <span>24/7</span>
                </div>
                <div style={{
                  fontSize: '11px',
                  color: 'rgba(255, 255, 255, 0.85)',
                  lineHeight: '1.3'
                }}>
                  A calming session • 3-5 min
                </div>
              </div>
            </div>
            
            <div style={{
              padding: '16px 20px',
              background: '#ffffff',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{
                background: 'rgba(243, 244, 246, 0.93)',
                borderRadius: '32px',
                padding: '6px 20px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(0, 0, 0, 0.06)'
              }}>
                <button
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    cursor: 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                    <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
                    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
                  </svg>
                </button>

                <button
                  onClick={() => setShowReflectionAudio(true)}
                  style={{
                    width: '52px',
                    height: '52px',
                    borderRadius: '50%',
                    background: '#000000',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.background = '#1a1a1a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.background = '#000000';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>

                <button
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    cursor: 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path 
                      d="M12 3l1.545 4.698a1.5 1.5 0 00.956.956L19.2 10.2l-4.698 1.545a1.5 1.5 0 00-.956.956L12 17.4l-1.545-4.698a1.5 1.5 0 00-.956-.956L4.8 10.2l4.698-1.545a1.5 1.5 0 00.956-.956L12 3z" 
                      fill="#78a5ffff"
                    />
                    <path 
                      d="M18.5 3l0.6 1.8a0.5 0.5 0 00.318.318L21.2 5.7l-1.8 0.6a0.5 0.5 0 00-.318.318L18.5 8.4l-0.6-1.8a0.5 0.5 0 00-.318-.318L15.8 5.7l1.8-0.6a0.5 0.5 0 00.318-.318L18.5 3z" 
                      fill="#7b79ffff"
                    />
                    <path 
                      d="M6 16l0.5 1.5a0.4 0.4 0 00.254.254L8.25 18.25l-1.5 0.5a0.4 0.4 0 00-.254.254L6 20.5l-0.5-1.5a0.4 0.4 0 00-.254-.254L3.75 18.25l1.5-0.5a0.4 0.4 0 00.254-.254L6 16z" 
                      fill="#9b89ffff"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// Latest Entry Tab Component
const LatestEntryTab = () => {
  if (entries.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#9ca3af'
      }}>
        <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ margin: '0 auto 16px', opacity: 0.5 }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"/>
        </svg>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
          No entries yet
        </h3>
        <p style={{ fontSize: '13px', color: '#9ca3af', lineHeight: '1.5' }}>
          Start writing to see your latest entries here
        </p>
      </div>
    );
  }

  return (
    <div style={{paddingBottom: '40px'}}>
      {/* Header */}
      <div style={{ marginBottom: '22px' }}>
      </div>

      {/* Latest Entries Grid - 4 entries (2 per row) */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        width: '100%',
        marginBottom: '16px'
      }}>
        <style>{`
          @media (min-width: 768px) {
            .latest-entries-container {
              display: grid !important;
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 20px !important;
            }
          }
          
          /* Hide entries 2, 3, 4 on mobile */
          @media (max-width: 767px) {
            .entry-card-2,
            .entry-card-3,
            .entry-card-4 {
              display: none !important;
            }
          }
        `}</style>
        
        <div className="latest-entries-container" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          width: '100%'
        }}>
          {entries.slice(0, 4).map((entry, index) => {
            const isLocked = isEntryLocked(entry.id);
            const images = loadEntryImages(entry);
            
            return (
              <div 
                key={entry.id} 
                className={`entry-card-${index + 1} affirmation-card`}
                style={{
                  cursor: 'pointer',
                  marginBottom: '0',
                  touchAction: 'manipulation',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                  overflow: 'hidden'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isLocked) {
                    setCurrentLockAction({ entryId: entry.id, action: 'view' });
                    setShowPinModal(true);
                    setPinInput('');
                  } else {
                    setSelectedEntry(entry);
                    setShowSingleEntry(true);
                  }
                }}
              >
                {/* Lock Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLock(entry.id);
                  }}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    zIndex: 10,
                    background: isLocked ? '#efefefff' : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: isLocked ? '1px solid #00000008' : '1px solid rgba(0, 0, 0, 0.1)',
                    padding: '8px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    touchAction: 'manipulation',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  title={isLocked ? 'Unlock entry' : 'Lock entry'}
                >
                  {isLocked ? <Lock size={16} color="#000000ff" /> : <Unlock size={16} color="#000000ff" />}
                </button>
                
                <div className="affirmation-inner">
                  {/* Image Grid Display */}
                  <div className="affirmation-image-container" style={{ position: 'relative', height: 'auto' }}>
                    <div className="affirmation-tag">
                      {index === 0 ? 'Latest Entry' : `Entry ${index + 1}`}
                    </div>
                    
                    {images && images.length > 0 ? (
                      <div style={{ width: '100%', position: 'relative' }}>
                        {images.length === 1 ? (
                          // Single image - full width
                          <img 
                            src={images[0].data} 
                            alt={entry.title || 'Entry'}
                            style={{
                              width: '100%',
                              height: '200px',
                              objectFit: 'cover',
                              display: 'block',
                              filter: isLocked ? 'blur(8px)' : 'none',
                              pointerEvents: 'none',
                              userSelect: 'none'
                            }}
                          />
                        ) : images.length === 2 ? (
                          // Two images - side by side
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '1px',
                            height: '200px'
                          }}>
                            {images.map((img, idx) => (
                              <img
                                key={idx}
                                src={img.data}
                                alt={`${entry.title} ${idx + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  filter: isLocked ? 'blur(8px)' : 'none',
                                  pointerEvents: 'none'
                                }}
                              />
                            ))}
                          </div>
                        ) : (
                          // Three images - Pinterest style
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr',
                            gridTemplateRows: '1fr 1fr',
                            gap: '1px',
                            height: '200px'
                          }}>
                            <img
                              src={images[0].data}
                              alt={`${entry.title} 1`}
                              style={{
                                gridRow: '1 / 3',
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                filter: isLocked ? 'blur(8px)' : 'none',
                                pointerEvents: 'none'
                              }}
                            />
                            {images.slice(1, 3).map((img, idx) => (
                              <img
                                key={idx}
                                src={img.data}
                                alt={`${entry.title} ${idx + 2}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  filter: isLocked ? 'blur(8px)' : 'none',
                                  pointerEvents: 'none'
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      // Fallback: Show background image if no image grid
                      entry.background && (
                        <img 
                          src={entry.background} 
                          alt={entry.title || 'Entry'} 
                          className="affirmation-image"
                          style={{
                            filter: isLocked ? 'blur(8px)' : 'none',
                            pointerEvents: 'none',
                            userSelect: 'none'
                          }}
                        />
                      )
                    )}
                  </div>

                  <div className="affirmation-content">
                    <div className="affirmation-title" style={{
                      filter: isLocked ? 'blur(4px)' : 'none'
                    }}>
                      {isLocked ? '••••••••••••' : (entry.title || 'Untitled')}
                    </div>
                    <div className="affirmation-message" style={{
                      filter: isLocked ? 'blur(4px)' : 'none'
                    }}>
                      {isLocked ? '•••••••••••••••••••••••••••••••••••••••••••••••••••••••••' : ((entry.content || '').substring(0, 150) + '...')}
                    </div>
                    
                    {/* Date and Time */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px', 
                      fontSize: '11px', 
                      color: '#9ca3af', 
                      marginTop: '8px'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={12} />
                        {formatDate ? formatDate(entry.date) : new Date(entry.date).toLocaleDateString()}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={12} />
                        {new Date(entry.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    
                    {/* Tags - Colored Style */}
                    {entry.tags && entry.tags.length > 0 && !isLocked && (
                      <div style={{
                        display: 'flex',
                        gap: '6px',
                        flexWrap: 'wrap',
                        marginTop: '8px'
                      }}>
                        {entry.tags.slice(0, 3).map((tag, tagIndex) => {
                          const tagColors = [
                            { bg: '#fef3c7', color: '#92400e' },
                            { bg: '#dbeafe', color: '#1e40af' },
                            { bg: '#d1fae5', color: '#065f46' }
                          ];
                          const colorIndex = tagIndex % 3;
                          const tagColor = tagColors[colorIndex];
                          
                          return (
                            <span
                              key={tagIndex}
                              style={{
                                padding: '4px 10px',
                                background: tagColor.bg,
                                color: tagColor.color,
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '500'
                              }}
                            >
                              {tag}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    
                    {isLocked && (
                      <div style={{
                        marginTop: '12px',
                        padding: '8px 12px',
                        background: 'rgba(82, 82, 82, 0.1)',
                        borderRadius: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '12px',
                        color: '#000000ff',
                        fontWeight: '500'
                      }}>
                        <Lock size={12} />
                        Tap to unlock and view
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
  return (
    <>
{/* Tab Header */}
<div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
  <div style={{
    display: 'flex',
    background: '#f1f1f1ff',
    borderRadius: '24px',
    border: '1.5px solid #ffffffff',
    padding: '4px',
    width: '100%',
    marginTop:'10px',
    maxWidth: '780px',
  }}>
    {[
      { id: 'latest', label: 'Journal' },
      { id: 'meditation', label: 'Meditation' },
      { id: 'foryou', label: 'For You' },
    ].map(({ id, label }) => (
      <button
        key={id}
        onClick={() => setActiveJournalTab(id)}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px 12px',
          borderRadius: '20px',
          border: 'none',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: '500',
          transition: 'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
          background: activeJournalTab === id ? 'white' : 'transparent',
          color: activeJournalTab === id ? '#111' : '#6b7280',
          boxShadow: activeJournalTab === id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </button>
    ))}
  </div>
</div>

      {/* Tab Content */}
{activeJournalTab === 'latest' ? (
  <LatestEntryTab />
) : activeJournalTab === 'foryou' ? (
  <ForYouTab />
) : (
  <MeditationTab />
)}
{showReflectionAudio && (
  <JournalReflectionAudio
    entries={entries}
    userName={userName}
    isEntryLocked={isEntryLocked} // ✅ ADD THIS
    onClose={() => setShowReflectionAudio(false)}
  />
)}

    </>
  );
}